import React, { useEffect, useState } from 'react';

import {
  QButton,
  QFormControl,
  QInput,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  useToastProvider,
  QCloseButton,
  QAlert,
  useCurrentUser,
} from '@qualio/ui-components';
import supplierApi from 'api/supplier.api';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

export type ImportModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetchSuppliers: () => void;
};

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, setIsOpen, refetchSuppliers }) => {
  const { t } = useTranslation();
  const [templatePrefix, setTemplatePrefix] = useState<string>();
  const [templateValue, setTemplateValue] = useState<string>();

  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();

  const {
    data: docs,
    isLoading: isSearching,
    refetch: refetchDocs,
    isRefetching,
  } = useQuery('qualioDocs', () => supplierApi.getQualioDocsByPrefix(companyId, templateValue as string), {
    retry: false,
    enabled: false,
  });
  const { mutateAsync: importSuppliersMutation, isLoading: isImporting } = useMutation(
    (documentIds: number[]) => {
      return supplierApi.importSuppliers(companyId, documentIds);
    },
    {
      onSuccess: () => {
        showToast({
          title: t('toastMessages.import.success'),
          description: t('toastMessages.import.successDescription'),
          status: 'success',
          'data-cy': 'toast-import',
        });
      },
      onError: () => {
        showToast({
          title: t('toastMessages.import.failed'),
          description: t('toastMessages.import.failedDescription'),
          status: 'error',
        });
      },
    },
  );

  const handleCloseModal = () => {
    setTemplateValue('');
    setTemplatePrefix('');
    setIsOpen(false);
  };

  useEffect(() => {
    if (templatePrefix) refetchDocs();
  }, [templatePrefix, refetchDocs]);

  const updateTemplatePrefix = () => {
    setTemplatePrefix(templateValue);
    refetchDocs();
  };

  const importSuppliers = async (docs: number[]) => {
    await importSuppliersMutation(docs);
    setIsOpen(false);
    setTemplateValue('');
    setTemplatePrefix('');
    await refetchSuppliers();
  };

  return (
    <QModal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
      <QModalHeader>
        <QText>{t('importModal.title')}</QText>
        <QCloseButton onClick={() => handleCloseModal()} />
      </QModalHeader>
      <QModalBody>
        <QStack direction="column">
          <QText fontSize={'sm'} mb={2}>
            {t('importModal.description')}
          </QText>
          <QFormControl
            label={t('importModal.fields.templatePrefix.label')}
            size="sm"
            helper={t('importModal.fields.templatePrefix.helper')}
            pb={4}
          >
            <QInput
              //TO-DO Fix controlled input console error
              width="80%"
              value={templateValue}
              onChange={(e: any) => setTemplateValue(e.target.value)}
              mr={8}
            />
            <QButton
              variant="ghost"
              size="sm"
              isLoading={isSearching || isRefetching}
              onClick={() => updateTemplatePrefix()}
            >
              {t('importModal.fields.templatePrefix.button')}
            </QButton>
          </QFormControl>

          {!!templatePrefix &&
            !isSearching &&
            !isRefetching &&
            (docs?.length ? (
              <QAlert
                description={t('importModal.messages.found', { count: docs?.length, prefix: templatePrefix })}
                status={'success'}
              ></QAlert>
            ) : (
              <QAlert
                description={t('importModal.messages.notFound', { prefix: templatePrefix })}
                status={'error'}
              ></QAlert>
            ))}
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton onClick={() => handleCloseModal()} variant="outline">
          {t('importModal.buttons.cancel')}
        </QButton>
        <QButton
          variant="solid"
          isLoading={isImporting}
          isDisabled={!docs?.length}
          onClick={() => importSuppliers(docs || [])}
        >
          {t('importModal.buttons.import')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default ImportModal;
